import React, { useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Command, CommandEmpty, CommandInput, CommandItem, CommandList,
} from '../../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { COUNTRIES } from './countries';

/**
 * Country picker — searchable, keyboard-driven, one dataset.
 *
 * WHAT THIS REPLACES. A native `<select>` holding all 249 ISO-3166 entries.
 * It worked, and it was unusable: finding "United Kingdom" meant scrolling a
 * 249-row menu, and type-ahead in a native select matches only the START of an
 * option, so typing "king" matched nothing at all. Country is REQUIRED at
 * creation and drives the tenant's timezone, language, currency and fiscal
 * year, so it is not a field to make people hunt for.
 *
 * ONE DATASET. `./countries.js` is generated from `pytz.country_names` — the
 * same data the platform's regional-default resolver reads — so the form can
 * never offer a code the resolver does not know. A second list here would be a
 * second answer to "which countries exist"; there is exactly one import.
 *
 * THE ASYMMETRY IS DELIBERATE AND IS PRESERVED. This component stores and
 * emits the ISO alpha-2 CODE and displays the NAME. `CreateTenantPage` sends
 * `country_code` (the code) and `company_address.country` (the display name) —
 * two fields with two consumers: the resolver keys on the code, and the
 * address is a postal artefact a human reads. Emitting the name here would
 * quietly change what the resolver receives.
 */

/**
 * Lowercase, accent-stripped. `"Côte d'Ivoire"` -> `"cote d'ivoire"`.
 *
 * Nobody types the circumflex. Without this, "cote", "curacao", "reunion",
 * "sao tome" and "aland" all match NOTHING, which reads as a missing country
 * rather than as a missing accent. NFD splits a letter from its combining
 * mark; the range strips the marks and leaves the base letter.
 */
const fold = (s) => (s || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

/**
 * Extra words a country can be found by. FOR SEARCH ONLY.
 *
 * FOUND BY WRITING THE TEST. The acceptance criterion was "typing king
 * surfaces United Kingdom" — and it did not, because `countries.js` is
 * generated from `pytz.country_names`, where GB is `"Britain (UK)"`. So the
 * one country an operator is most likely to type in full was unreachable by
 * its own name.
 *
 * The fix belongs HERE and not in `countries.js`: that file is generated from
 * the platform resolver's own data, and editing display names in it would put
 * this form and the resolver on different spellings. An alias is a way of
 * FINDING a row; it is never displayed, never stored, and never emitted — the
 * option still reads "Britain (UK)" and still submits `GB`.
 *
 * Deliberately short. Every entry is a name a person would actually type for a
 * country whose canonical spelling does not contain it. Anything already a
 * substring of the canonical name (Burma, Swaziland, Macedonia) needs nothing.
 */
export const SEARCH_ALIASES = {
  GB: 'united kingdom uk great britain england scotland wales northern ireland',
  US: 'usa united states of america',
  AE: 'uae emirates',
  KR: 'south korea',
  KP: 'north korea',
  NL: 'holland',
  CZ: 'czechia',
  CI: 'ivory coast',
  CD: 'drc democratic republic of the congo zaire',
  TR: 'turkiye',
  TL: 'timor-leste',
  VA: 'holy see',
  MM: 'myanmar',
};

/**
 * Case- and accent-insensitive substring, over the name and its aliases.
 *
 * NOT the code. Matching two-letter codes turns every query into noise: "zw"
 * would surface Zimbabwe out of nowhere, and "at" would put Austria above
 * every country whose name contains "at". The code is an identifier the
 * operator does not think in; the name is what they are looking for.
 */
export const matchesCountry = (country, query) => {
  const q = fold(query).trim();
  if (!q) return true;
  if (fold(country.name).includes(q)) return true;
  const alias = SEARCH_ALIASES[country.code];
  return !!alias && alias.includes(q);
};

export default function CountryCombobox({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  // `useId` rather than a module-level counter: two of these on one screen
  // would otherwise share an id and the second trigger's `aria-controls` would
  // point at the first one's popup.
  const popupId = `country-combobox-${useId()}`;

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === value) || null,
    [value],
  );

  // FILTERED BEFORE RENDER, and `shouldFilter={false}` below is the other half
  // of the same decision. cmdk's own filter scores EVERY registered item on
  // every keystroke; with 249 of them that is 249 fuzzy scores per character,
  // and it also reorders the list by score, so the answer moves while you are
  // still typing. A plain substring filter is one pass, keeps the alphabetical
  // order the operator is scanning, and renders only the matches — with a
  // query present the list is the matches and nothing else.
  //
  // Memoised on the query, so re-rendering for `open` does not re-filter.
  const matches = useMemo(
    () => COUNTRIES.filter((c) => matchesCountry(c, query)),
    [query],
  );

  const choose = (code) => {
    onChange?.(code);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // The query is a way of finding an option, not a value. Leaving it
        // behind means the next open shows yesterday's four matches and looks
        // like a broken catalogue.
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        {/* `role="combobox"` on the TRIGGER, which is what a closed combobox
            is. Radix would otherwise announce `aria-haspopup="dialog"` — true
            of the popover as a mechanism, wrong about what this control is.

            MEASURED, NOT ASSUMED: cmdk (1.1.1) spreads caller props BEFORE its
            own, so the search input inside carries `role="combobox"` too and
            that cannot be overridden. That is the shadcn Command-in-Popover
            pattern as shipped, and the two never coexist visibly — the trigger
            is the control while closed, the input is the control while open —
            but it is a real property of this implementation rather than
            something to claim is clean.

            `aria-controls` names the POPUP, because cmdk generates the
            listbox's id internally and does not let a caller set it. The popup
            contains the listbox, which is the relationship a screen reader
            follows. */}
        <button
          type="button"
          ref={triggerRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={popupId}
          aria-haspopup="listbox"
          disabled={disabled}
          data-testid="country-select"
          className="flex w-full h-9 items-center justify-between rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={selected ? 'truncate text-foreground' : 'truncate text-muted-foreground'}>
            {selected ? selected.name : 'Select country...'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      {/* Width matched to the trigger so the popup reads as the field opening
          rather than as a menu that happens to be nearby. */}
      <PopoverContent
        id={popupId}
        align="start"
        className="w-[--radix-popover-trigger-width] p-0"
        data-testid="country-combobox-popup"
      >
        <Command shouldFilter={false} label="Country">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search countries…"
            data-testid="country-combobox-input"
          />
          <CommandList data-testid="country-combobox-list">
            <CommandEmpty>No country matches that search.</CommandEmpty>
            {matches.map((c) => (
              <CommandItem
                key={c.code}
                // The CODE is the identity. cmdk lowercases and trims the
                // value it hands back to `onSelect`, so the country is closed
                // over here instead — a lowercased alpha-2 would be the wrong
                // thing to store and the bug would only show for the codes
                // that survive lowercasing.
                value={c.code}
                onSelect={() => choose(c.code)}
                data-testid={`country-option-${c.code}`}
              >
                <Check
                  className={`mr-2 h-4 w-4 ${c.code === value ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden="true"
                />
                {c.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
